import type { SectionType, WorkoutPlanInfoId } from '@az3fr/core';
import { PlusIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';

import { sectionPlanSliceSelectors } from '@/entities/section-plan/model/section-plan.selector';
import { workoutCreatorThunks } from '@/features/workout-creator/model';
import { ChooseSectionTypeViewV2 } from '@/features/workout-creator/ui/v2/choose-section-type-view-v2';
import { useAppDispatch, useAppSelector } from '@/hooks/store.hook';

interface AddSectionButtonV2Props {
	workoutPlanId: WorkoutPlanInfoId;
}

/**
 * A button component that allows users to add new sections to a workout plan.
 * Shows a simple button initially, then expands to show section type selection when clicked.
 */
function AddSectionButtonV2({ workoutPlanId }: AddSectionButtonV2Props) {
	const dispatch = useAppDispatch();
	const [isSelecting, setIsSelecting] = useState(false);

	const sectionPlans = useAppSelector((state) =>
		sectionPlanSliceSelectors.selectAllByWorkoutPlanId(state, workoutPlanId),
	);
	const hasExistingSections = sectionPlans.length > 0;

	function handleAddSectionClick() {
		setIsSelecting(true);
	}

	function handleSectionTypeSelected(sectionType: SectionType) {
		dispatch(
			workoutCreatorThunks.addSectionPlan({
				workoutPlanId,
				sectionType,
			}),
		);
		setIsSelecting(false);
	}

	function handleCancel() {
		setIsSelecting(false);
	}

	if (isSelecting) {
		return (
			<ChooseSectionTypeViewV2 onTypeSelected={handleSectionTypeSelected} onCancel={handleCancel} />
		);
	}

	if (!hasExistingSections) {
		return (
			<button
				type="button"
				onClick={handleAddSectionClick}
				className="group flex w-full flex-col items-center justify-center gap-3 rounded-xl border-2 border-gray-300 border-dashed bg-white py-10 transition-all hover:border-gray-400 hover:bg-gray-50"
			>
				<div className="flex size-12 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors group-hover:bg-gray-900 group-hover:text-white">
					<PlusIcon className="size-6" />
				</div>
				<div className="text-center">
					<span className="block font-semibold text-gray-900 text-sm sm:text-base">
						Add First Section
					</span>
					<span className="mt-0.5 block font-medium text-gray-500 text-xs">
						Start building your workout plan
					</span>
				</div>
			</button>
		);
	}

	return (
		<button
			type="button"
			onClick={handleAddSectionClick}
			className="group flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 font-medium text-gray-600 text-sm shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900"
		>
			<PlusIcon className="size-5 opacity-70 group-hover:opacity-100" />
			<span>Add another section</span>
		</button>
	);
}

export { AddSectionButtonV2 };
